import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { authApi } from '@/shared/api/apiEndpoints'
import { clearSessionExpired, onSessionExpired } from '@/shared/authSession'
import { decodeJwtExpMs } from '@/shared/jwt'
import { getStoredToken, removeStoredToken, setStoredToken } from '@/shared/tokenStore'
import { useTenantConfig } from '@/contexts/TenantConfigContext'
import type { AuthUser, CookieLoginResponse, LoginRequest } from '@/types'

interface AuthState {
  user: AuthUser | null
  token: string | null
  isLoading: boolean
  /** axiosClient가 401을 맞아 markSessionExpired()를 부른 뒤 true — SessionExpiredModal이 이 값을 본다.
   *  `user`가 null이면(애초에 로그인 상태가 아니었던 경우) 무시한다 — PrivateRoute가 알아서 /login으로 보낸다. */
  sessionExpired: boolean
}

interface AuthContextValue extends AuthState {
  login: (data: LoginRequest) => Promise<void>
  /** 쿠키 로그인 — 쿠키에 저장된 JWT(이 앱 또는 관리 화면)를 서버가 검증하고 이 앱의 새 JWT를 발급한다.
   *  실패 사유는 반환값의 resultCode로 확인 */
  cookieLogin: () => Promise<CookieLoginResponse>
  /** 로그아웃. cookie 모드이고 logoutUrl이 있으면 JWT 삭제 후 그 외부 페이지로 이동한다(호출부는 /login으로 보내지 말 것) */
  logout: () => Promise<void>
  /** 테넌트 설정의 authMode가 cookie인지 (brochure) */
  isCookieAuth: boolean
  /** 화면을 떠나지 않고 비밀번호만 다시 받아 토큰을 교체한다(SessionExpiredModal 전용) */
  reauthenticate: (password: string) => Promise<void>
  isAuthenticated: boolean
  canWrite: boolean      // userLevel <= 4
  isAdmin: boolean       // userLevel === 0
}

const AuthContext = createContext<AuthContextValue | null>(null)

// 토큰 유효시간은 24시간(백엔드 EgovJwtTokenUtil.JWT_TOKEN_VALIDITY) — 만료 30분 전에 조용히
// 재발급해 두면 정상적으로 앱을 계속 쓰는 한 401 자체가 거의 발생하지 않는다(2026-09-22, 사용자 요청,
// "1순위(만료돼도 데이터 전송)+2순위(조용한 재발급)" 중 2순위).
const REFRESH_MARGIN_MS = 30 * 60 * 1000
// 재발급이 네트워크 오류 등으로 실패하면(= 아직 만료 전인데 서버에 못 닿은 경우) 5분 뒤 다시 시도한다.
// 이미 만료돼서 실패한 경우(401)는 axiosClient가 이미 markSessionExpired()를 불러 모달이 뜬 상태이므로
// 여기서 또 재시도해도 똑같이 401만 반복될 뿐 해롭지 않다.
const REFRESH_RETRY_MS = 5 * 60 * 1000

// 이 탭에서 직접 로그아웃했는지 — 쿠키 로그인은 관리 화면 토큰도 인정하므로, 로그아웃 직후 로그인 화면이
// 자동 쿠키 로그인을 하면 관리 화면 사용자로 곧바로 다시 들어와 로그아웃이 안 된 것처럼 보인다.
// 이 표시가 있으면 LoginPage가 자동 쿠키 로그인을 건너뛰고 폼을 보여준다(폼으로 로그인하면 지운다).
// sessionStorage라 탭을 닫으면 사라진다 — 새 창에서 다시 열면 평소처럼 관리 화면 로그인을 이어받는다.
const LOGGED_OUT_KEY = 'recorder:loggedOut'
const markLoggedOut = () => { try { sessionStorage.setItem(LOGGED_OUT_KEY, '1') } catch { /* 저장소 사용 불가 */ } }
const clearLoggedOut = () => { try { sessionStorage.removeItem(LOGGED_OUT_KEY) } catch { /* 저장소 사용 불가 */ } }
export const wasLoggedOutInThisTab = (): boolean => {
  try { return sessionStorage.getItem(LOGGED_OUT_KEY) === '1' } catch { return false }
}

type LoginLikeResult = { token?: string; user?: AuthUser; resultMessage?: string }

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // TenantConfigProvider가 AuthProvider 바깥에 있으므로(App.tsx) 여기서 바로 읽을 수 있다.
  const { authMode, logoutUrl } = useTenantConfig()
  const isCookieAuth = authMode === 'cookie'

  const [state, setState] = useState<AuthState>({
    user: null,
    token: getStoredToken(),
    isLoading: true,
    sessionExpired: false,
  })

  const refreshTimer = useRef<number>()
  // scheduleRefresh와 silentRefresh는 서로를 참조한다(예약된 타이머가 만료되면 재발급하고, 재발급
  // 성공 시 다음 예약을 건다) — useCallback 두 개를 직접 서로의 deps로 얽으면 선언 순서 문제가 생기므로
  // ref로 최신 silentRefresh를 들고 있다가 타이머 콜백에서는 ref를 통해서만 부른다.
  const silentRefreshRef = useRef<() => Promise<void>>()

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== undefined) {
      window.clearTimeout(refreshTimer.current)
      refreshTimer.current = undefined
    }
  }, [])

  const scheduleRefresh = useCallback((token: string) => {
    clearRefreshTimer()
    const expMs = decodeJwtExpMs(token)
    if (expMs === undefined) return // 토큰 형식을 못 읽으면 예약하지 않는다 — 401이 오면 모달이 잡아준다
    const delay = Math.max(expMs - Date.now() - REFRESH_MARGIN_MS, 0)
    refreshTimer.current = window.setTimeout(() => { void silentRefreshRef.current?.() }, delay)
  }, [clearRefreshTimer])

  const silentRefresh = useCallback(async () => {
    try {
      const res = await authApi.refresh() as unknown as LoginLikeResult
      if (!res.token) throw new Error(res.resultMessage ?? '재발급 실패')
      setStoredToken(res.token)
      clearSessionExpired()
      setState((s) => ({ ...s, token: res.token!, user: res.user ?? s.user, sessionExpired: false }))
      scheduleRefresh(res.token)
    } catch {
      // 실패 이유는 보통 "이미 만료됨"이다(만료된 토큰으로는 /auth/refresh 자체가 401을 받는다) —
      // 그 경우 axiosClient의 401 인터셉터가 이미 markSessionExpired()를 호출해 재인증 모달이 떠 있다.
      // 네트워크 문제 등 다른 이유로 실패했을 수도 있으니 잠시 뒤 한 번 더 시도한다.
      refreshTimer.current = window.setTimeout(() => { void silentRefreshRef.current?.() }, REFRESH_RETRY_MS)
    }
  }, [scheduleRefresh])

  useEffect(() => { silentRefreshRef.current = silentRefresh }, [silentRefresh])

  // 쿠키 로그인: 서버가 쿠키의 JWT(이 앱 토큰, 그리고 관리 화면 토큰)를 서명·만료로 검증하고 이 앱의 새 JWT를
  // 발급한다 — 관리 화면에 로그인해 있으면 여기서 다시 로그인하지 않아도 된다. 예전에는 외부 쿠키(hiball_token)의
  // accessUserId 만 믿어서 쿠키를 직접 만들면 남의 아이디로 로그인할 수 있었다.
  // 받은 토큰은 아이디/비밀번호 로그인과 똑같이 저장·예약 갱신한다.
  const cookieLogin = useCallback(async (): Promise<CookieLoginResponse> => {
    const res = await authApi.cookieLogin()
    if (res.result === 'TRUE' && res.token) {
      setStoredToken(res.token)
      clearSessionExpired()
      setState({ user: res.user!, token: res.token, isLoading: false, sessionExpired: false })
      scheduleRefresh(res.token)
    }
    return res
  }, [scheduleRefresh])

  const cookieLoginRef = useRef(cookieLogin)
  useEffect(() => { cookieLoginRef.current = cookieLogin }, [cookieLogin])

  // 세션 만료 신호 구독 — axiosClient가 401을 맞으면 여기로 알려준다(shared/authSession.ts).
  // cookie 모드는 비밀번호를 되묻는 대신 쿠키로 조용히 재발급을 먼저 시도하고, 그것도 실패할 때만 모달을 띄운다.
  useEffect(() => onSessionExpired(() => {
    if (!isCookieAuth) {
      setState((s) => ({ ...s, sessionExpired: true }))
      return
    }
    cookieLoginRef.current()
      .then((res) => {
        if (res.result !== 'TRUE') setState((s) => ({ ...s, sessionExpired: true }))
      })
      .catch(() => setState((s) => ({ ...s, sessionExpired: true })))
  }), [isCookieAuth])

  // 탭이 오래 백그라운드에 있다 돌아왔을 때를 위한 보조 장치 — 노트북 절전 등으로 예약된
  // setTimeout이 늦게 발화하거나 건너뛰었을 수 있으니, 화면이 다시 보일 때 남은 유효시간을 한 번 더
  // 확인해 이미 재발급 구간에 들어섰으면 즉시 시도한다.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const token = getStoredToken()
      if (!token) return
      const expMs = decodeJwtExpMs(token)
      if (expMs !== undefined && Date.now() >= expMs - REFRESH_MARGIN_MS) {
        void silentRefreshRef.current?.()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  // 페이지 새로고침 시 토큰으로 사용자 복원
  useEffect(() => {
    const token = getStoredToken()
    if (!token) {
      setState((s) => ({ ...s, isLoading: false }))
      return
    }
    const loggedOut = () => {
      removeStoredToken()
      setState({ user: null, token: null, isLoading: false, sessionExpired: false })
    }
    const restoreWithMe = () => {
      authApi
        .me()
        .then((user) => {
          clearSessionExpired()
          setState({ user, token, isLoading: false, sessionExpired: false })
          scheduleRefresh(token)
        })
        .catch(loggedOut)
    }
    // 쿠키 로그인 모드: 저장된 토큰을 서버가 서명·만료로 검증받고 새 토큰으로 바꾼다. 관리 화면이나 외부 사이트에서
    // 다른 계정으로 다시 로그인했으면 서버가 그 사용자로 바꾸거나(관리 화면 토큰이 더 최근) 거절한다(외부 쿠키와 다름) —
    // 예전에는 화면이 기록입력 화면에서만 외부 쿠키 사용자와 비교했다(삭제한 shared/hiballCookie.ts).
    // 거절되면 이전 사용자 토큰을 쓰지 않고 로그아웃 상태로 둔다(LoginPage가 사유를 보여준다).
    // 네트워크 오류면 기존처럼 /auth/me 로 복원을 시도한다.
    if (isCookieAuth) {
      cookieLogin()
        .then((res) => {
          if (res.result === 'TRUE') return // cookieLogin이 토큰·사용자·갱신 예약까지 마쳤다
          if (res.resultCode === 'DISABLED') restoreWithMe()
          else loggedOut()
        })
        .catch(restoreWithMe)
      return
    }
    restoreWithMe()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scheduleRefresh·cookieLogin은 마운트 시 한 번만 필요하다
  }, [])

  const login = useCallback(async (data: LoginRequest) => {
    const res = await authApi.login(data) as unknown as LoginLikeResult
    if (!res.token) {
      throw new Error(res.resultMessage ?? '아이디 또는 비밀번호가 올바르지 않습니다.')
    }
    clearLoggedOut()
    setStoredToken(res.token)
    clearSessionExpired()
    setState({ user: res.user!, token: res.token, isLoading: false, sessionExpired: false })
    scheduleRefresh(res.token)
  }, [scheduleRefresh])

  // 화면(입력 중이던 값·오프라인 큐)은 그대로 둔 채 비밀번호만 다시 받아 토큰을 교체한다
  // (SessionExpiredModal 전용, 2026-09-22 사용자 요청 — "1순위"). login()과 달리 아이디를 다시 입력받지
  // 않는다 — 이미 누가 로그인해 있었는지 알고 있으니 비밀번호만 확인하면 된다.
  const reauthenticate = useCallback(async (password: string) => {
    const username = state.user?.username
    if (!username) {
      throw new Error('로그인 정보를 찾을 수 없습니다 — 새로고침 후 다시 로그인해 주십시오.')
    }
    const res = await authApi.login({ username, password }) as unknown as LoginLikeResult
    if (!res.token) {
      throw new Error(res.resultMessage ?? '비밀번호가 올바르지 않습니다.')
    }
    setStoredToken(res.token)
    clearSessionExpired()
    setState({ user: res.user!, token: res.token, isLoading: false, sessionExpired: false })
    scheduleRefresh(res.token)
  }, [state.user, scheduleRefresh])

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {})
    clearRefreshTimer()
    clearSessionExpired()
    removeStoredToken()
    markLoggedOut()
    setState({ user: null, token: null, isLoading: false, sessionExpired: false })
    // cookie 모드에 외부 로그아웃 페이지가 있으면 그리로 보낸다(외부 사이트 로그인까지 함께 끝낸다).
    // 관리 화면 토큰 쿠키가 아직 유효하면 다음에 이 앱을 열 때 그 사용자로 다시 들어온다(로그인 공유).
    if (isCookieAuth && logoutUrl) {
      window.location.replace(logoutUrl)
    }
  }, [clearRefreshTimer, isCookieAuth, logoutUrl])

  const value: AuthContextValue = {
    ...state,
    login,
    cookieLogin,
    logout,
    isCookieAuth,
    reauthenticate,
    isAuthenticated: !!state.user,
    canWrite: !!state.user && state.user.userLevel <= 4,
    isAdmin: !!state.user && state.user.userLevel === 0,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('AuthProvider가 없습니다.')
  return ctx
}
